import React, { useEffect, forwardRef, useImperativeHandle, useState } from 'react';
import { useFormik, getIn } from 'formik';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';

import { formatNumberThsndAndDec, formatNumberMagnitude } from '../helpers/stringFormat';
import { getMagnitude, formatString } from './valueFunctions';
import { addCurrencyFrequency } from '../helpers/currencyFormat';
import { Typography } from '@mui/material';
import { GreenRedCompactSwitch, TextFieldCustom } from '../helpers/formFields';
import firstCharToUpper from '../helpers/stringFormat';
import { createSliderMarks, getSliderInitialValue, getRatio } from './formFunctions';
import { toolTipUserIcon } from "./iconFunctions";
import { tooltip, generalTooltipText } from '../helpers/tooltip';

const CostValueDriverLaborForm = forwardRef(({ currency, outcome, outcomeList, selectedCost, costStructure, editLaborDriverId, editLaborDriver, formActions, onSubmit }, ref) => {
    // const [shouldSubmit, setShouldSubmit] = useState(false);
    const [open, setOpen] = useState(false);
    const [openOutcome, setOpenOutcome] = useState(false);
    const [selectedType, setSelectedType] = useState(costStructure.multiple_type[0])
    const [positiveDriver, setPositiveDriver] = useState(true);

    const tooltipText = {
        hoursSaved: `Number of hours ${positiveDriver ? 'saved' : 'extra'} per employee of this type each month relative to your competitive alternative (the one stated when you created your Product).`,
        occurrence: "Occurrence indicates the percentage of the selected employee type experiencing the efficiency change (gain or loss). E.g., 100% means all employees of that type are affected; 50% means half are.",
        ratio: "This value represents the estimated percentage of a company's total workforce that typically holds this role within the industry.",
    }

    const handleTypeChange = (event) => {
        setSelectedType(event.target.value);
        formik.setFieldValue('multiple_type', event.target.value)
        formik.setFieldValue('cost_per_multiple', getSliderInitialValue(costStructure, event.target.value))
        formik.setFieldValue('ratio', getRatio(costStructure, event.target.value))
        formik.setFieldValue('driver_name', `${event.target.value} ${positiveDriver ? 'Labor Savings' : 'Extra Labor'}`)
    };

    const handleOutcomeChange = (event) => {
        formik.setFieldValue('outcome', event.target.value)
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleOpen = () => {
        setOpen(true);
    };

    const handleOutcomeClose = () => {
        setOpenOutcome(false);
    };

    const handleOutcomeOpen = () => {
        setOpenOutcome(true);
    };

    const shortenDriverName = (inputString) => {
        // Remove 'staff'
        let result = inputString.replace(/\bstaff\b/gi, '');

        // Shorten 'Research & Development' to 'R&D'
        result = result.replace(/Research and Development/gi, 'R&D');

        // Shorten 'Quality Control' to 'QC'
        result = result.replace(/Quality Control/gi, 'QC');

        // Remove extra spaces after replacing staff
        result = result.trim().replace(/\s+/g, ' ');

        return result;
    };


    useEffect(() => {
        setSelectedType(costStructure.multiple_type[0])
        formik.setFieldValue("multiple_type", costStructure.multiple_type[0])
        formik.setFieldValue("cost_per_multiple", getSliderInitialValue(costStructure, costStructure.multiple_type[0]))
        formik.setFieldValue("ratio", getRatio(costStructure, costStructure.multiple_type[0]))
        // formik.setFieldValue("driver_name", `${costStructure.multiple_type[0]} ${positiveDriver ? 'Labor Savings' : 'Extra Labor'}`)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [costStructure]);

    useEffect(() => {
        formik.setFieldValue("outcome", outcomeList[0])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [outcomeList]);

    useEffect(() => {
        if (editLaborDriverId) {
            setPositiveDriver(editLaborDriver["positive_driver"])

            formik.setFieldValue("multiple_type", editLaborDriver["multiple_type"])
            formik.setFieldValue("cost_per_multiple", editLaborDriver["cost_per_multiple"])
            formik.setFieldValue("hours_saved", editLaborDriver["hours_saved"])
            formik.setFieldValue("occurrence", editLaborDriver["occurrence"])
            formik.setFieldValue("ratio", editLaborDriver["ratio"] || 0)
            formik.setFieldValue("multiple_value", editLaborDriver["multiple_value"])
            formik.setFieldValue("positive_driver", editLaborDriver["positive_driver"])
            formik.setFieldValue("outcome", editLaborDriver["outcome"])
            formik.setFieldValue("driver_name", editLaborDriver["driver_name"])
            formik.setFieldValue("comment", editLaborDriver["comment"])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editLaborDriverId])

    useEffect(() => {
        formik.setFieldValue("driver_name", `${selectedType} ${positiveDriver ? 'Labor Savings' : 'Extra Labor'}`)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [positiveDriver, selectedType])

    const initialValues = {
        id: null,
        cost_type: 'labor',
        outcome: '',
        driver_name: `${selectedType} ${positiveDriver ? 'Labor Savings' : 'Extra Labor'}`,
        comment: '',
        positive_driver: true,
        type: costStructure.type,
        currency: currency,
        multiple_type: selectedType,
        multiple: costStructure.multiple,
        multiple_value: 0,
        cost_per_multiple: getSliderInitialValue(costStructure, selectedType),
        hours_saved: 0,
        occurrence: 100,
        ratio: getRatio(costStructure, selectedType),
    }

    const isNumeric = (value) => {
        return /^-?\d{1,3}(,\d{3})*(\.\d+)?$/.test(value);
    };

    const isPositive = (value) => {
        return parseFloat(value.toString().replace(/,/g, '')) >= 0;
    };

    const validate = (values) => {
        const errors = {};
        if (!values.driver_name) {
            errors.driver_name = 'Driver must have a name';
        }
        if (!isNumeric(values.hours_saved)) {
            errors.hours_saved = 'Must be numeric value';
        } else if (!isPositive(values.hours_saved)) {
            errors.hours_saved = 'Must be positive or zero';
        }
        if (!isNumeric(values.occurrence)) {
            errors.occurrence = 'Must be numeric value';
        } else if (!isPositive(values.occurrence)) {
            errors.occurrence = 'Must be positive or zero';
        } else if (values.occurrence > 100) {
            errors.occurrence = 'Not more than 100';
        }

        return errors
    }

    const formik = useFormik({
        initialValues: initialValues,
        validate: validate,
        onSubmit: onSubmit,
    });

    useImperativeHandle(ref, () => ({
        resetForm() {
            formik.handleReset()
            setSelectedType(costStructure.multiple_type[0])
            setPositiveDriver(true)
            if (!outcome) {
                formik.setFieldValue("outcome", outcomeList[0] || '')
            }
            formik.setFieldValue("multiple_type", costStructure.multiple_type[0])
            formik.setFieldValue("cost_per_multiple", getSliderInitialValue(costStructure, costStructure.multiple_type[0]))
        },
    }));

    return (
        <Box component="form" onSubmit={formik.handleSubmit} sx={{ ml: 2, maxWidth: 1200, mt: 2 }}>
            <Grid container columnSpacing={2} alignItems="center" rowSpacing={3}>
                {costStructure.multiple_type.map(type => {
                    if (type === selectedType) {
                        return (
                            <React.Fragment key={type}>
                                <Grid item xs={12}>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                        2. Labor costs
                                    </Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Stack direction="column" gap={1}>
                                        <Typography variant="caption">
                                            Employee Type
                                        </Typography>
                                        <Box>
                                            <Select
                                                open={open}
                                                onClose={handleClose}
                                                onOpen={handleOpen}
                                                value={formik.values.multiple_type}
                                                onChange={handleTypeChange}
                                                size='small'
                                            >
                                                {costStructure.multiple_type.map((type) => (
                                                    <MenuItem key={type} value={type}>
                                                        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                                            {firstCharToUpper(type)}
                                                            {costStructure.cost_per_multiple[type]?.user_created && toolTipUserIcon()}
                                                        </Box>
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </Box>
                                    </Stack>
                                </Grid>
                                <Grid item xs={10} />
                                <Grid item xs={3}>
                                    <Stack direction="column" gap={1}>
                                        <Typography variant="caption">
                                            {`Annual cost per ${costStructure.multiple}`}
                                        </Typography>
                                        < Slider
                                            name="cost_per_multiple"
                                            value={formik.values.cost_per_multiple}
                                            onChange={formik.handleChange}
                                            valueLabelDisplay="auto"
                                            marks={createSliderMarks(costStructure.cost_per_multiple[type])}
                                            min={costStructure.cost_per_multiple[type].lower * 0}
                                            max={costStructure.cost_per_multiple[type].higher * 1.2}
                                            step={getMagnitude(costStructure.cost_per_multiple[type].lower) / 10}
                                            valueLabelFormat={value => addCurrencyFrequency(formatNumberMagnitude(value), costStructure.cost_per_multiple[type].currency, null)}
                                            sx={{ maxWidth: 300 }}
                                        />
                                    </Stack>
                                </Grid>

                                <Grid item xs={6} />
                                <Grid item xs={12}>
                                    <Grid container columnSpacing={2}>
                                        <Grid item xs={2}>
                                            <Typography variant="caption" sx={{ mr: 1 }}>
                                                Hours {positiveDriver ? 'saved' : 'extra'} per employee and month{tooltip(tooltipText.hoursSaved)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={2} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                                            <Typography variant="caption" sx={{ mr: 1 }}>
                                                Occurrence{tooltip(tooltipText.occurrence)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={2} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                                            <Typography variant="caption" sx={{ mr: 1 }}>
                                                % of Total Employees{tooltip(tooltipText.ratio)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6} />
                                        <Grid item xs={2}>
                                            <TextFieldCustom
                                                name="hours_saved"
                                                endAdornment={
                                                    <Typography variant="caption">
                                                        hours
                                                    </Typography>
                                                }
                                                value={formik.values.hours_saved}
                                                onChange={(event) => {
                                                    const { value } = event.target
                                                    const formattedValue = formatNumberThsndAndDec(value)
                                                    formik.setFieldValue("hours_saved", formattedValue || '')
                                                }}
                                                onBlur={formik.handleBlur}
                                                error={getIn(formik.errors, "hours_saved") && Boolean(getIn(formik.errors, "hours_saved"))}
                                                helperText={getIn(formik.touched, "hours_saved") && getIn(formik.errors, "hours_saved")}
                                                greenBackground={positiveDriver}
                                                redBackground={!positiveDriver}
                                            />
                                        </Grid>
                                        <Grid item xs={2}>
                                            <TextFieldCustom
                                                name="occurrence"
                                                endAdornment="%"
                                                value={formik.values.occurrence}
                                                onChange={(event) => {
                                                    const { value } = event.target
                                                    const formattedValue = formatNumberThsndAndDec(value)
                                                    formik.setFieldValue("occurrence", formattedValue || '')
                                                }}
                                                onBlur={formik.handleBlur}
                                                error={getIn(formik.errors, "occurrence") && Boolean(getIn(formik.errors, "occurrence"))}
                                                helperText={getIn(formik.touched, "occurrence") && getIn(formik.errors, "occurrence")}
                                            />
                                        </Grid>
                                        <Grid item xs={2}>
                                            <TextFieldCustom
                                                name="ratio"
                                                endAdornment="%"
                                                value={formik.values.ratio}
                                                onChange={(event) => {
                                                    const { value } = event.target
                                                    const formattedValue = formatNumberThsndAndDec(value)
                                                    formik.setFieldValue("ratio", formattedValue || '')
                                                }}
                                                onBlur={formik.handleBlur}
                                                error={getIn(formik.errors, "ratio") && Boolean(getIn(formik.errors, "ratio"))}
                                                helperText={getIn(formik.touched, "ratio") && getIn(formik.errors, "ratio")}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                        3. Driver details
                                    </Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <Stack direction="column" gap={1}>
                                        <Typography variant="caption" sx={{ mr: 1 }}>
                                            Value driver{tooltip(generalTooltipText.differentiationValue)}
                                        </Typography>
                                        <GreenRedCompactSwitch
                                            name="positive_driver"
                                            leftLabel="Negative"
                                            rightLabel="Positive"
                                            value={formik.values.positive_driver}
                                            onChange={(event) => {
                                                formik.handleChange(event)
                                                setPositiveDriver(!positiveDriver)
                                            }}
                                        />

                                    </Stack>
                                </Grid>
                                <Grid item xs={10} />
                                {!outcome &&
                                    (<React.Fragment>
                                        <Grid item xs={2}>
                                            <Stack direction="column" gap={1}>
                                                <Typography variant="caption">
                                                    Outcome
                                                </Typography>
                                                <Box>
                                                    <Select
                                                        open={openOutcome}
                                                        onClose={handleOutcomeClose}
                                                        onOpen={handleOutcomeOpen}
                                                        value={formik.values.outcome}
                                                        onChange={handleOutcomeChange}
                                                        size='small'
                                                    >
                                                        {outcomeList.map((outcome) => (
                                                            <MenuItem key={outcome} value={outcome}>
                                                                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                                                    {outcome}
                                                                </Box>
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </Box>
                                            </Stack>
                                        </Grid>
                                        <Grid item xs={10} />
                                    </React.Fragment>)
                                }
                                <Grid item xs={12}>
                                    <Grid container columnSpacing={2}>
                                        <Grid item xs={3}>
                                            <Typography variant="caption" sx={{ mr: 1 }}>
                                                Name of driver
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={5}>
                                            <Typography variant="caption" sx={{ mr: 1 }}>
                                                Comment
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={4} />
                                        <Grid item xs={3}>
                                            <TextFieldCustom
                                                name="driver_name"
                                                value={formik.values.driver_name}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={getIn(formik.errors, "driver_name") && Boolean(getIn(formik.errors, "driver_name"))}
                                                helperText={
                                                    (getIn(formik.touched, "driver_name") && getIn(formik.errors, "driver_name")) ||
                                                        (getIn(formik.errors, "driver_name") && getIn(formik.errors, "driver_name").includes("already"))
                                                        ? getIn(formik.errors, "driver_name")
                                                        : ''
                                                }
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={5}>
                                            <TextFieldCustom
                                                name="comment"
                                                value={formik.values.comment}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={getIn(formik.errors, "comment") && Boolean(getIn(formik.errors, "comment"))}
                                                helperText={getIn(formik.touched, "comment") && getIn(formik.errors, "comment")}
                                                fullWidth
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </React.Fragment>
                        )
                    }
                    else {
                        return null;
                    }
                })
                }
            </Grid>
            <Box sx={{ mt: 3, mb: 4 }}>
                {formActions()}
            </Box>
        </Box>
    )

})

export default CostValueDriverLaborForm